import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LevelUpOverlayProps {
  newLevel: number;
  accentColor: string;
  artistName: string;
  onComplete: () => void;
}

const LEVEL_CONFIG: Record<number, {
  title: string;
  subtitle: string;
  icon: string;
  color: string;
  glow: string;
  badge: string;
}> = {
  1: {
    title: 'RESONATOR',
    subtitle: '共鳴者',
    icon: 'ri-seedling-fill',
    color: '#4ade80',
    glow: 'rgba(74,222,128,0.6)',
    badge: '🌱',
  },
  2: {
    title: 'DEEP DIVER',
    subtitle: '深淵の探求者',
    icon: 'ri-fire-fill',
    color: '#fb923c',
    glow: 'rgba(251,146,60,0.6)',
    badge: '🔥',
  },
  3: {
    title: 'CORE FAN',
    subtitle: '核心の信奉者',
    icon: 'ri-vip-crown-fill',
    color: '#facc15',
    glow: 'rgba(250,204,21,0.7)',
    badge: '👑',
  },
};

export function LevelUpOverlay({ newLevel, accentColor, artistName, onComplete }: LevelUpOverlayProps) {
  const [phase, setPhase] = useState<'flash' | 'reveal' | 'badge' | 'hold' | 'fadeout'>('flash');
  const animFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const phaseRef = useRef(phase);

  const cfg = LEVEL_CONFIG[newLevel] ?? {
    title: 'LEGEND',
    subtitle: '伝説',
    icon: 'ri-star-fill',
    color: accentColor,
    glow: `${accentColor}99`,
    badge: '⭐',
  };

  // フェーズ管理
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('reveal'), 250);
    const t2 = setTimeout(() => setPhase('badge'), 900);
    const t3 = setTimeout(() => setPhase('hold'), 1600);
    const t4 = setTimeout(() => setPhase('fadeout'), 4000);
    const t5 = setTimeout(() => onComplete(), 4800);
    return () => { [t1, t2, t3, t4, t5].forEach(clearTimeout); };
  }, [onComplete]);

  // Canvas パーティクル
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    type Particle = {
      x: number; y: number; vx: number; vy: number;
      size: number; opacity: number; color: string;
      life: number; maxLife: number; shape: 'circle' | 'star';
    };

    const colors = [cfg.color, '#ffffff', accentColor, cfg.color + 'aa', '#fff8'];
    const particles: Particle[] = Array.from({ length: 100 }, (_, i) => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 5,
      vy: (Math.random() - 0.5) * 5 - 2.5,
      size: Math.random() * 7 + 2,
      opacity: Math.random() * 0.9 + 0.1,
      color: colors[i % colors.length],
      life: 0,
      maxLife: Math.random() * 140 + 60,
      shape: Math.random() > 0.6 ? 'star' : 'circle',
    }));

    const drawStar = (cx: number, cy: number, r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particles.forEach((p) => {
        if (p.life >= p.maxLife) return;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06;
        p.life += 1;
        const alpha = Math.max(0, p.opacity * (1 - p.life / p.maxLife));
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 12;
        ctx.shadowColor = p.color;
        if (p.shape === 'star') {
          drawStar(p.x, p.y, p.size);
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });
      if (alive || phaseRef.current !== 'fadeout') {
        animFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [cfg.color, accentColor]);

  const overlayOpacity = phase === 'fadeout' ? 0 : 1;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{
        opacity: overlayOpacity,
        transition: phase === 'fadeout' ? 'opacity 0.8s ease-out' : 'opacity 0.25s ease-in',
        pointerEvents: phase === 'fadeout' ? 'none' : 'auto',
        background: phase === 'flash'
          ? `radial-gradient(circle at center, ${cfg.color}70 0%, #000 65%)`
          : `radial-gradient(circle at center, ${cfg.color}18 0%, rgba(0,0,0,0.96) 70%)`,
        transition: `opacity ${phase === 'fadeout' ? '0.8s' : '0.25s'} ease`,
      }}
      onClick={onComplete}
    >
      {/* パーティクルCanvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ width: '100%', height: '100%' }}
      />

      {/* 放射グロー */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 55% 35% at 50% 50%, ${cfg.color}18 0%, transparent 70%)`,
        }}
      />

      {/* メインコンテンツ */}
      <div className="relative flex flex-col items-center text-center px-8 select-none">

        {/* ── DEPTH LEVEL UP テキスト ── */}
        <motion.p
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: phase !== 'flash' ? 1 : 0, y: phase !== 'flash' ? 0 : -12 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="text-xs tracking-[0.45em] uppercase mb-4 font-bold"
          style={{ color: cfg.color, textShadow: `0 0 24px ${cfg.glow}` }}
        >
          ✦ DEPTH LEVEL UP ✦
        </motion.p>

        {/* ── レベル数字 ── */}
        <motion.div
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: phase !== 'flash' ? 1 : 0.4, opacity: phase !== 'flash' ? 1 : 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.15 }}
          className="relative mb-2"
        >
          {/* 背景装飾数字 */}
          <span
            className="absolute inset-0 flex items-center justify-center font-mono font-black pointer-events-none"
            style={{
              fontSize: 'clamp(130px, 32vw, 260px)',
              color: 'transparent',
              WebkitTextStroke: `1px ${cfg.color}12`,
              zIndex: 0,
            }}
          >
            {newLevel}
          </span>
          {/* メイン数字 */}
          <span
            className="relative font-mono font-black leading-none"
            style={{
              fontSize: 'clamp(90px, 22vw, 180px)',
              color: cfg.color,
              textShadow: `0 0 50px ${cfg.glow}, 0 0 100px ${cfg.color}50, 0 0 150px ${cfg.color}25`,
              WebkitTextStroke: `1px ${cfg.color}`,
              zIndex: 1,
            }}
          >
            {newLevel}
          </span>
        </motion.div>

        {/* ── バッジカード ── */}
        <AnimatePresence>
          {(phase === 'badge' || phase === 'hold' || phase === 'fadeout') && (
            <motion.div
              initial={{ scale: 0.6, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className="relative flex flex-col items-center gap-3 px-10 py-5 rounded-2xl mb-4"
              style={{
                background: `linear-gradient(135deg, ${cfg.color}18, ${cfg.color}08)`,
                border: `2px solid ${cfg.color}60`,
                boxShadow: `0 0 40px ${cfg.glow}, 0 0 80px ${cfg.color}20, inset 0 0 30px ${cfg.color}08`,
              }}
            >
              {/* パルスリング */}
              <motion.div
                className="absolute inset-0 rounded-2xl border-2 pointer-events-none"
                animate={{ scale: [1, 1.06, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                style={{ borderColor: cfg.color }}
              />

              {/* アイコン */}
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{
                  background: `${cfg.color}20`,
                  border: `2px solid ${cfg.color}70`,
                  boxShadow: `0 0 20px ${cfg.glow}`,
                }}
              >
                <i className={`${cfg.icon} text-3xl`} style={{ color: cfg.color }} />
              </div>

              {/* 称号 */}
              <div className="text-center">
                <p
                  className="font-mono text-2xl sm:text-3xl font-black tracking-[0.18em] leading-tight"
                  style={{ color: '#fff', textShadow: `0 0 20px ${cfg.color}80` }}
                >
                  {cfg.title}
                </p>
                <p
                  className="text-sm tracking-[0.25em] mt-1 font-bold"
                  style={{ color: cfg.color }}
                >
                  {cfg.subtitle}
                </p>
              </div>

              {/* アーティスト名 */}
              <div
                className="flex items-center gap-2 px-4 py-1.5 rounded-full"
                style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${cfg.color}30` }}
              >
                <i className="ri-music-2-line text-xs" style={{ color: `${cfg.color}99` }} />
                <span className="text-gray-300 text-xs tracking-widest">
                  {artistName} との深度が上がった
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── 解放特典ヒント ── */}
        <AnimatePresence>
          {(phase === 'hold') && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center gap-2 mb-4"
            >
              {newLevel === 1 && (
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-wider"
                  style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}40`, color: cfg.color }}
                >
                  <i className="ri-lock-unlock-line" />
                  LV.1限定 ECHOES が解放されました
                </div>
              )}
              {newLevel === 2 && (
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-wider"
                  style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}40`, color: cfg.color }}
                >
                  <i className="ri-lock-unlock-line" />
                  LV.2限定コンテンツが解放されました
                </div>
              )}
              {newLevel === 3 && (
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-wider"
                  style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}40`, color: cfg.color }}
                >
                  <i className="ri-vip-diamond-line" />
                  CREDITS掲載の権利を獲得しました
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── TAP TO CONTINUE ── */}
        <motion.p
          animate={{ opacity: phase === 'hold' ? [0.4, 0.8, 0.4] : 0 }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="text-gray-600 text-xs tracking-[0.3em] uppercase"
        >
          TAP TO CONTINUE
        </motion.p>
      </div>

      {/* 四隅装飾 */}
      {[
        'top-4 left-4 border-t-2 border-l-2 rounded-tl',
        'top-4 right-4 border-t-2 border-r-2 rounded-tr',
        'bottom-4 left-4 border-b-2 border-l-2 rounded-bl',
        'bottom-4 right-4 border-b-2 border-r-2 rounded-br',
      ].map((cls, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: phase !== 'flash' ? 0.45 : 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className={`absolute ${cls} w-8 h-8 pointer-events-none`}
          style={{ borderColor: cfg.color }}
        />
      ))}
    </div>
  );
}
